import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Shimmer from './Shimmer';

// Skeleton for TestDetail page. Keeps the real page's overall scaffold:
//   breadcrumb bar · forest-green hero · 2×(2-col key-value cards) ·
//   bottom CTA card. Breadcrumb stays real since it's rendered from
//   route params, not data.

// Card used for the two 2-col detail grids. Title band + 6 rows of
// label/value to mirror the real KeyValueRow list.
const SpecCardShimmer = ({ rows = 6 }) => (
  <Box sx={{ bgcolor: '#fff', border: '1px solid var(--border-l)', borderRadius: '16px', overflow: 'hidden' }}>
    {/* Section header band (dark) */}
    <Box sx={{ bgcolor: 'var(--forest)', p: '10px 18px' }}>
      <Shimmer width={160} height={10} rounded={4} sx={{ bgcolor: 'rgba(255,255,255,0.12)' }} />
    </Box>
    {Array.from({ length: rows }).map((_, i) => (
      <Box
        key={i}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '115px 1fr', md: '145px 1fr' },
          gap: { xs: 1, md: 1.5 },
          p: { xs: '10px 14px', md: '10px 18px' },
          borderBottom: i < rows - 1 ? '1px solid var(--border-l)' : 'none',
        }}
      >
        <Shimmer width="80%" height={11} rounded={4} />
        <Shimmer width={`${60 + (i * 7) % 35}%`} height={11} rounded={4} />
      </Box>
    ))}
  </Box>
);

const TestDetailShimmer = () => (
  <Box sx={{ bgcolor: 'var(--cream)', minHeight: '100vh' }}>
    {/* Breadcrumb bar */}
    <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid var(--border-l)', py: 1.75 }}>
      <Container maxWidth="xl" sx={{ maxWidth: 1300, px: { xs: 2, md: 4 } }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Shimmer width={80} height={11} rounded={4} />
          <Shimmer width={8} height={11} rounded={4} />
          <Shimmer width={140} height={11} rounded={4} />
        </Stack>
      </Container>
    </Box>

    <Container maxWidth="xl" sx={{ maxWidth: 1300, py: 4.5, px: { xs: 2, md: 4 } }}>
      {/* Hero — forest green card. Shimmers rendered on dark need a
          lighter, translucent tint so they don't blend into the bg. */}
      <Box
        sx={{
          bgcolor: 'var(--forest)',
          borderRadius: '20px',
          p: { xs: 3, md: 4 },
          mb: 2.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: { xs: 2, md: 3 },
          }}
        >
          {/* Icon tile */}
          <Shimmer width={64} height={64} rounded="14px" sx={{ bgcolor: 'rgba(255,255,255,0.08)', flexShrink: 0 }} />

          {/* Title block */}
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            <Shimmer width={80} height={18} rounded={100} sx={{ bgcolor: 'rgba(255,255,255,0.12)', mb: 1.25 }} />
            <Shimmer width="80%" height={32} rounded={6} sx={{ bgcolor: 'rgba(255,255,255,0.14)', mb: 1.25 }} />
            <Stack direction="row" spacing={1}>
              <Shimmer width={60} height={10} rounded={4} sx={{ bgcolor: 'rgba(255,255,255,0.10)' }} />
              <Shimmer width={70} height={10} rounded={4} sx={{ bgcolor: 'rgba(255,255,255,0.10)' }} />
              <Shimmer width={90} height={10} rounded={4} sx={{ bgcolor: 'rgba(255,255,255,0.10)' }} />
            </Stack>
          </Box>

          {/* Price + buttons */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ flexShrink: 0, width: { xs: '100%', md: 'auto' } }}>
            <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Shimmer width={80} height={26} rounded={6} sx={{ bgcolor: 'rgba(255,255,255,0.14)' }} />
            </Box>
            <Stack spacing={1} sx={{ flex: { xs: 1, md: 'initial' } }}>
              <Shimmer width={120} height={36} rounded={100} sx={{ bgcolor: 'rgba(201,125,8,0.45)' }} />
              <Shimmer width={120} height={30} rounded={100} sx={{ bgcolor: 'rgba(255,255,255,0.10)' }} />
            </Stack>
          </Stack>
        </Box>
      </Box>

      {/* Specs + Applicability */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2, mb: 2, width: '100%',
        }}
      >
        <SpecCardShimmer rows={6} />
        <SpecCardShimmer rows={6} />
      </Box>

      {/* Sample + Schedule */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2, mb: 2, width: '100%',
        }}
      >
        <SpecCardShimmer rows={4} />
        {/* Right card has day-pills + a few key-values instead of pure rows */}
        <Box sx={{ bgcolor: '#fff', border: '1px solid var(--border-l)', borderRadius: '16px', overflow: 'hidden' }}>
          <Box sx={{ bgcolor: 'var(--forest)', p: '10px 18px' }}>
            <Shimmer width={120} height={10} rounded={4} sx={{ bgcolor: 'rgba(255,255,255,0.12)' }} />
          </Box>
          <Box sx={{ p: '18px 18px 14px' }}>
            <Shimmer width={100} height={11} rounded={4} sx={{ mb: 1.5 }} />
            <Stack direction="row" spacing={0.75} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <Shimmer key={i} width={34} height={34} circular />
              ))}
            </Stack>
            {Array.from({ length: 3 }).map((_, i) => (
              <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '145px 1fr', gap: 1.5, p: '10px 0', borderBottom: i < 2 ? '1px solid var(--border-l)' : 'none' }}>
                <Shimmer width="80%" height={11} rounded={4} />
                <Shimmer width={`${70 + i * 5}%`} height={11} rounded={4} />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Accordion block placeholder — two collapsed rows */}
      <Box sx={{ mb: 2 }}>
        {Array.from({ length: 2 }).map((_, i) => (
          <Box
            key={i}
            sx={{
              bgcolor: '#fff',
              border: '1px solid var(--border-l)',
              borderRadius: '12px',
              mb: 1, px: 2.25, py: 1.75,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}
          >
            <Shimmer width={`${60 + i * 10}%`} height={12} rounded={4} />
            <Shimmer width={16} height={16} circular />
          </Box>
        ))}
      </Box>

      {/* Bottom CTA */}
      <Box
        sx={{
          bgcolor: 'var(--forest)',
          borderRadius: '16px',
          p: { xs: 2.5, md: 3 },
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box>
          <Shimmer width={140} height={18} rounded={4} sx={{ bgcolor: 'rgba(255,255,255,0.14)', mb: 0.75 }} />
          <Shimmer width={240} height={11} rounded={4} sx={{ bgcolor: 'rgba(255,255,255,0.10)' }} />
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <Shimmer width={80} height={22} rounded={6} sx={{ bgcolor: 'rgba(255,255,255,0.14)' }} />
          <Shimmer width={140} height={40} rounded={100} sx={{ bgcolor: 'rgba(201,125,8,0.45)' }} />
        </Stack>
      </Box>
    </Container>
  </Box>
);

export default TestDetailShimmer;
